import { Alert, AlertIcon, Box, Button, Flex, HStack, Tag, Text, Wrap, WrapItem } from '@chakra-ui/react'
import { ReactNode } from 'react'

import { FormField } from '../../../components/FormField'
import { Icon } from '../../../components/Icon'
import WysiwygContent from '../../../components/WysiwygContent'
import { IArtField } from '../../../services/Contests/interfaces'
import { iconForArtField } from './artFieldHelpers'
import { FIELD_TYPE_LABELS } from './ArtFieldsEditor'

interface IArtFieldsSummaryProps {
  fields: IArtField[]
  notice: string | null
  onEdit: () => void
  disabled?: boolean
  /** True when the API/validation returned errors for the schema. */
  hasErrors?: boolean
}

/** Fixed fields the student always sees, regardless of the configured schema. */
const FIXED_FIELDS: { icon: string; label: string }[] = [
  { icon: 'lni lni-image', label: 'Imagem da obra' },
  { icon: 'lni lni-pencil', label: 'Título da obra' },
  { icon: 'lni lni-tag', label: 'Disponível para leilão' }
]

/** Soft, rounded chip for the field state tags — calmer than Chakra's uppercase Badge. */
function MetaPill({ colorScheme, children }: { colorScheme: string; children: ReactNode }) {
  return (
    <Tag size="sm" colorScheme={colorScheme} variant="subtle" borderRadius="full" fontWeight="medium">
      {children}
    </Tag>
  )
}

/** Small label that introduces a group, with an optional trailing count. */
function GroupLabel({ children, count }: { children: ReactNode; count?: ReactNode }) {
  return (
    <Flex align="center" justify="space-between" mb={2}>
      <Text variant="label" letterSpacing="0.02em">
        {children}
      </Text>
      {count != null && <Text variant="caption">{count}</Text>}
    </Flex>
  )
}

/**
 * Read-only summary of the artwork data questions shown inside the contest form
 * (ABRA-84). Editing happens in a dedicated modal (with live preview) opened via
 * the button here — this keeps the contest form uncluttered while still telling
 * the admin, at a glance, which questions exist and how each is configured.
 */
export default function ArtFieldsSummary({ fields, notice, onEdit, disabled, hasErrors }: IArtFieldsSummaryProps) {
  return (
    <Box>
      <FormField label="Campos de dados das obras" mb={1}>
        <Text variant="caption" mb={4}>
          Resumo das perguntas que o participante responde ao enviar cada obra. Para alterar perguntas, tipos, ícones e
          ordem — e visualizar o formulário — use o botão abaixo.
        </Text>
      </FormField>

      {hasErrors && (
        <Alert status="error" rounded="lg" mb={4} fontSize="sm">
          <AlertIcon />
          Há erros nos campos de dados das obras. Abra o editor para corrigir.
        </Alert>
      )}

      {/* Fixed fields — always present, not configurable */}
      <GroupLabel>Campos fixos · sempre exibidos</GroupLabel>
      <Wrap spacing={2} mb={5}>
        {FIXED_FIELDS.map((f) => (
          <WrapItem key={f.label}>
            <Tag size="sm" variant="subtle" colorScheme="gray" borderRadius="full" color="gray.600" px={3} py={1}>
              <Icon name={f.icon} mr={1.5} color="gray.400" />
              {f.label}
            </Tag>
          </WrapItem>
        ))}
      </Wrap>

      {/* Configurable questions */}
      <GroupLabel
        count={fields.length > 0 ? `${fields.length} ${fields.length === 1 ? 'pergunta' : 'perguntas'}` : undefined}
      >
        Perguntas personalizadas
      </GroupLabel>

      {fields.length === 0 ? (
        <Flex
          direction="column"
          align="center"
          gap={1.5}
          border="1px dashed"
          borderColor="gray.200"
          borderRadius="lg"
          px={4}
          py={6}
          bg="gray.50"
        >
          <Icon name="lni lni-clipboard" color="gray.300" size="xl" />
          <Text variant="caption">Nenhuma pergunta personalizada configurada.</Text>
        </Flex>
      ) : (
        <Flex direction="column" gap={2}>
          {fields.map((field) => {
            const locations = [
              field.show_in_listing && 'Listagem',
              field.show_in_identifier && 'Identificador',
              field.show_in_labels && 'Etiquetas'
            ].filter(Boolean) as string[]

            return (
              <Flex
                key={field.key}
                align="flex-start"
                gap={3}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="lg"
                p={3}
                bg="white"
                transition="border-color 0.15s ease, box-shadow 0.15s ease"
                _hover={{ borderColor: 'magenta.200', boxShadow: 'xs' }}
              >
                {/* Tinted icon tile */}
                <Flex
                  align="center"
                  justify="center"
                  boxSize={9}
                  borderRadius="lg"
                  bg="magenta.50"
                  color="magenta.500"
                  flexShrink={0}
                >
                  <Icon name={iconForArtField(field)} size="lg" />
                </Flex>

                {/* Label + quiet meta line */}
                <Box flex="1" minW={0}>
                  <Text fontSize="sm" fontWeight="semibold" color="gray.800" noOfLines={1}>
                    {field.label?.trim() || (
                      <Text as="span" color="gray.400" fontStyle="italic" fontWeight="normal">
                        Sem rótulo
                      </Text>
                    )}
                  </Text>
                  <Flex align="center" gap={1.5} mt={0.5} color="gray.500" fontSize="xs" flexWrap="wrap">
                    <Text>{FIELD_TYPE_LABELS[field.type]}</Text>
                    {locations.length > 0 && (
                      <>
                        <Box as="span" color="gray.300">
                          •
                        </Box>
                        <Icon name="lni lni-eye" color="gray.400" />
                        <Text>{locations.join(', ')}</Text>
                      </>
                    )}
                  </Flex>
                </Box>

                {/* State tags — kept to the essential, with a single accent for "required" */}
                {(field.required || field.standard) && (
                  <Wrap spacing={1} justify="flex-end" flexShrink={0} shouldWrapChildren>
                    {field.required && <MetaPill colorScheme="magenta">Obrigatório</MetaPill>}
                    {field.standard && <MetaPill colorScheme="purple">Padrão</MetaPill>}
                  </Wrap>
                )}
              </Flex>
            )
          })}
        </Flex>
      )}

      {/* Notice preview */}
      {notice && notice.trim() && (
        <HStack
          align="flex-start"
          spacing={2}
          mt={4}
          p={3}
          borderRadius="lg"
          border="1px solid"
          borderColor="magenta.100"
          bg="magenta.50"
        >
          <Icon name="lni lni-warning" color="magenta.500" mt="2px" flexShrink={0} />
          <Box flex="1" minW={0}>
            <Text fontSize="xs" fontWeight="semibold" color="magenta.600" mb={0.5}>
              Aviso exibido no formulário
            </Text>
            <WysiwygContent
              content={notice}
              fontSize="xs"
              color="gray.600"
              sx={{ '& p': { margin: 0 }, '& p + p': { marginTop: 1 }, '& ul, & ol': { my: 1, pl: 4 } }}
            />
          </Box>
        </HStack>
      )}

      <Button
        mt={5}
        colorScheme="magenta"
        leftIcon={<Icon name="lni lni-pencil-alt" />}
        onClick={onEdit}
        isDisabled={disabled}
      >
        Editar campos de dados das obras
      </Button>
    </Box>
  )
}
